import React, { createContext, useContext, useEffect, useMemo, useState, useCallback } from "react";

const CartContext = createContext(null);
const STORAGE_KEY = "mimes-cart-v1";

export const CartProvider = ({ children }) => {
  const [items, setItems] = useState(() => {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      return raw ? JSON.parse(raw) : [];
    } catch {
      return [];
    }
  });
  const [isOpen, setIsOpen] = useState(false);
  const [shakeTick, setShakeTick] = useState(0);

  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(items));
  }, [items]);

  const addItem = useCallback((product, qty = 1) => {
    setItems((prev) => {
      const found = prev.find((i) => i.product_id === product.id);
      if (found) {
        return prev.map((i) =>
          i.product_id === product.id ? { ...i, quantity: Math.min(i.quantity + qty, 50) } : i
        );
      }
      return [
        ...prev,
        {
          product_id: product.id,
          name: product.name,
          price_eur: product.price_eur,
          image: product.image,
          slug: product.slug,
          weight_g: product.weight_g,
          quantity: qty,
        },
      ];
    });
    setShakeTick((t) => t + 1);
  }, []);

  const removeItem = useCallback((product_id) => {
    setItems((prev) => prev.filter((i) => i.product_id !== product_id));
  }, []);

  const updateQuantity = useCallback((product_id, quantity) => {
    setItems((prev) =>
      prev
        .map((i) => (i.product_id === product_id ? { ...i, quantity: Math.max(1, Math.min(50, quantity)) } : i))
        .filter((i) => i.quantity > 0)
    );
  }, []);

  const clear = useCallback(() => setItems([]), []);

  const { subtotal, count } = useMemo(() => {
    const s = items.reduce((acc, i) => acc + i.price_eur * i.quantity, 0);
    const c = items.reduce((acc, i) => acc + i.quantity, 0);
    return { subtotal: s, count: c };
  }, [items]);

  const value = {
    items,
    addItem,
    removeItem,
    updateQuantity,
    clear,
    subtotal,
    count,
    isOpen,
    setIsOpen,
    shakeTick,
  };

  return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
};

export const useCart = () => {
  const ctx = useContext(CartContext);
  if (!ctx) throw new Error("useCart must be used inside CartProvider");
  return ctx;
};
